import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Stato dei filtri tenuto nei parametri dell'URL (replace: true), così
 * sopravvive a refresh e tasto indietro senza sporcare la cronologia.
 *
 * Un parametro assente vale il suo default; un parametro presente ma vuoto
 * (`?status=`) vale stringa vuota — serve quando il default non è "tutti"
 * (es. admin, che apre su "Da pagare").
 */
export function useUrlFilters<K extends string>(defaults: Record<K, string>) {
  const [searchParams, setSearchParams] = useSearchParams();

  const values = useMemo(() => {
    const out = {} as Record<K, string>;
    for (const key of Object.keys(defaults) as K[]) {
      out[key] = searchParams.has(key) ? (searchParams.get(key) ?? '') : defaults[key];
    }
    return out;
    // defaults è un oggetto letterale costante per ogni pagina
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const setFilters = useCallback(
    (patch: Partial<Record<K, string>>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch) as [K, string | undefined][]) {
            if (value === undefined || value === defaults[key]) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearchParams]
  );

  const reset = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const key of Object.keys(defaults)) next.delete(key);
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSearchParams]);

  const isDirty = (Object.keys(defaults) as K[]).some((key) => values[key] !== defaults[key]);

  return { values, setFilters, reset, isDirty };
}

/**
 * Input di ricerca con debounce: l'utente digita in locale, l'URL si aggiorna
 * 300 ms dopo. Se l'URL cambia da fuori (indietro, "Azzera filtri") l'input si
 * riallinea.
 */
export function useDebouncedSearch(urlValue: string, onCommit: (value: string) => void, delay = 300) {
  const [text, setText] = useState(urlValue);

  useEffect(() => {
    setText(urlValue);
  }, [urlValue]);

  useEffect(() => {
    const trimmed = text.trim();
    if (trimmed === urlValue) return;
    const timeout = window.setTimeout(() => onCommit(trimmed), delay);
    return () => window.clearTimeout(timeout);
    // onCommit cambia a ogni render del chiamante: conta solo il testo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, urlValue, delay]);

  return [text, setText] as const;
}
